import { useState } from 'react'
import { useNavigate, generatePath } from 'react-router-dom'
import { ITableViewWithColumns } from 'types'
import { TABLE } from 'routes'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { hydrateView } from 'components/spreadsheet/helpers/hydrateView'
import { compareNames } from 'helpers/compare'

const createdTime = (view: ITableViewWithColumns): number =>
  view.createdAt ? new Date(view.createdAt).getTime() : Number.MAX_SAFE_INTEGER

const VIEW_ORDER = {
  created: (a: ITableViewWithColumns, b: ITableViewWithColumns): number => createdTime(a) - createdTime(b),
  name: (a: ITableViewWithColumns, b: ITableViewWithColumns): number => compareNames(a.name, b.name)
}

/**
 * Which of a table's views to list, and how they nest. Shared by the view menu
 * and the table page's view tabs, so the two never disagree.
 *
 * Inside a document only the original view and its personal children show.
 * Personal views that belong to another view are grouped under it; everything
 * else is a top-level view. By default views are listed in the order they were
 * made, so a new tab goes at the end; any without a creation time keep their
 * order, last. The view menu lists them by name instead, so a view is easy to
 * find (case aside, and "View 2" before "View 10").
 */
export const groupViews = (
  views: ITableViewWithColumns[],
  inProcess: boolean,
  originalViewId: string | null | undefined,
  search = '',
  order: keyof typeof VIEW_ORDER = 'created'
): { parentViews: ITableViewWithColumns[]; personalViewsByParent: Record<string, ITableViewWithColumns[]> } => {
  const filteredViews = views.filter((view) => view.name.toLowerCase().includes(search)).sort(VIEW_ORDER[order])

  const viewsToDisplay =
    inProcess && originalViewId
      ? filteredViews.filter(
          (view) =>
            view.publicId === originalViewId ||
            (view.isPersonalView && view.configurationParentView?.publicId === originalViewId)
        )
      : filteredViews

  const parentViews = viewsToDisplay.filter((view) => !view.isPersonalView || !view.configurationParentView)

  const personalViewsByParent = viewsToDisplay
    .filter((view) => view.isPersonalView && view.configurationParentView)
    .reduce(
      (acc, view) => {
        const parentId = view.configurationParentView?.publicId
        if (!parentId) return acc
        if (!acc[parentId]) acc[parentId] = []
        acc[parentId].push(view)
        return acc
      },
      {} as Record<string, ITableViewWithColumns[]>
    )

  return { parentViews, personalViewsByParent }
}

/**
 * Switch the table to another view: confirm first if an admin has unsaved
 * changes, load the view's columns if they haven't been yet, then show it (and,
 * on the table's own page, put it in the URL).
 *
 * `selectView` resolves true once the view is shown, false if the user stayed
 * or loading it failed.
 */
export const useSelectView = (): {
  selectView: (viewId: string) => Promise<boolean>
  hydratingViewId: string | null
} => {
  const { spreadsheetData, setSpreadsheetData, inProcess } = useDataContext()
  const { displayErrorMessage } = useApplicationStore()
  const { project } = useProject()
  const navigate = useNavigate()
  const [hydratingViewId, setHydratingViewId] = useState<string | null>(null)

  const selectView = async (viewId: string): Promise<boolean> => {
    if (spreadsheetData.userConfiguration.unsavedChanges && spreadsheetData.isAdmin) {
      const shouldLeave = window.confirm(
        'There are unsaved changes - are you sure you wish to navigate away from this page?'
      )
      if (!shouldLeave) return false
    }

    let fetchedView: ITableViewWithColumns | undefined
    let fetchedParent: ITableViewWithColumns | null = null
    const target = spreadsheetData.tableView.find((view) => view.publicId === viewId)

    if (!target?.columnsHydrated) {
      setHydratingViewId(viewId)
      try {
        const result = await hydrateView(viewId, spreadsheetData.tableView)
        fetchedView = result.view
        fetchedParent = result.parent
      } catch (error) {
        displayErrorMessage(error)
        setHydratingViewId(null)
        return false
      }
      setHydratingViewId(null)
    }

    if (fetchedParent) {
      setSpreadsheetData({ type: 'HYDRATE_VIEW', viewDetails: fetchedParent })
    }
    setSpreadsheetData({ type: 'SET_VIEW', viewId, viewDetails: fetchedView, inProcess })

    if (!inProcess) {
      navigate(
        generatePath(TABLE, {
          projectId: project.publicId,
          tableId: spreadsheetData.tableDetails.publicId,
          viewId
        }),
        { replace: true }
      )
    }
    return true
  }

  return { selectView, hydratingViewId }
}
